import { createContext, use } from "react";

import { createAuth } from "./auth.ts";
import { createCache } from "./cache.ts";
import { createApiClient } from "./client.ts";
import { createFactors } from "./factors.ts";
import { clearPropertyContexts } from "./property-context.ts";
import { createLive } from "./realtime.ts";
import { clearRecents } from "./recents.ts";
import { createSessionKeeper, createSessions } from "./sessions.ts";

import type { Auth } from "./auth.ts";
import type { Cache } from "./cache.ts";
import type { ApiClient } from "./client.ts";
import type { Factors } from "./factors.ts";
import type { PushOutcome, PushStatus } from "./push.ts";
import type { Live } from "./realtime.ts";
import type { SessionKeeper, Sessions } from "./sessions.ts";
import type { Environment } from "../environment.ts";
import type { ReactElement, ReactNode } from "react";

export interface DataLayer {
  readonly client: ApiClient;
  readonly auth: Auth;
  readonly cache: Cache;
  readonly live: Live;
  readonly factors: Factors;
  readonly sessions: Sessions;
  readonly keeper: SessionKeeper;
  readonly push: DevicePush;
}

interface DevicePush {
  readonly status: () => Promise<PushStatus>;
  readonly enable: (label: string) => Promise<PushOutcome>;
}

const loadPush = async () => import("./push.ts");

const releaseDevice = async (client: ApiClient): Promise<void> => {
  try {
    const { releasePush } = await loadPush();
    await releasePush(client);
  } catch {
    // WHY: a push chunk that fails to load (offline, a deploy) never blocks sign-out; the session's end drops the device.
  }
};

const forgetDevice = async (): Promise<void> => {
  try {
    const { forgetPush } = await loadPush();
    await forgetPush();
  } catch {
    // WHY: the server already dropped the device with its session; the browser forgets it on the next sign-out.
  }
};

// WHY: the push code loads on first use; signing out first removes this device's subscription while the session still works.
const withPushRelease = (auth: Auth, client: ApiClient): Auth => ({
  ...auth,
  signOut: async (reason) => {
    await releaseDevice(client);
    await auth.signOut(reason);
  },
});

export const createDataLayer = (environment: Environment, release: string): DataLayer => {
  const client = createApiClient(environment);
  const auth = withPushRelease(createAuth(client), client);
  const cache = createCache(release);
  const live = createLive(client, cache.queryClient);
  const keeper = createSessionKeeper(client, auth);
  auth.onSignedOut(() => {
    keeper.stop();
    live.stop();
    void cache.clear();
    void clearPropertyContexts();
    clearRecents();
    void forgetDevice();
  });
  return {
    client,
    auth,
    cache,
    live,
    keeper,
    factors: createFactors(client),
    sessions: createSessions(client, keeper),
    push: {
      status: async () => {
        const [{ pushStatus }, user] = await Promise.all([loadPush(), auth.currentUser()]);
        return pushStatus(environment.VITE_VAPID_PUBLIC_KEY, user?.id ?? null);
      },
      enable: async (label) => {
        const [{ enablePush }, user] = await Promise.all([loadPush(), auth.currentUser()]);
        return user === null
          ? "failed"
          : enablePush({ client, vapidKey: environment.VITE_VAPID_PUBLIC_KEY, label, userId: user.id });
      },
    },
  };
};

const DataLayerContext = createContext<DataLayer | null>(null);
DataLayerContext.displayName = "DataLayerContext";

export const DataLayerProvider = ({
  value,
  children,
}: {
  readonly value: DataLayer;
  readonly children: ReactNode;
}): ReactElement => <DataLayerContext value={value}>{children}</DataLayerContext>;

export const useDataLayer = (): DataLayer => {
  const dataLayer = use(DataLayerContext);
  if (dataLayer === null) {
    throw new Error("useDataLayer needs a DataLayerProvider");
  }
  return dataLayer;
};
